/*
 * Regras das telas de planos com versões (BBF-107), como o protótipo v2 (`telas-planos.js`) e o app-funcionalidades
 * da BBF-104:
 *  - versão publicada não muda; a mudança vai para um rascunho copiado de uma versão, um por plano;
 *  - publicar tira a anterior de venda, e ela segue só para quem já assina;
 *  - trial só com cartão entre os meios aceitos: sem cartão, o trial zera;
 *  - o módulo é incluído, opcional com preço, ou fica fora (ausente da versão).
 */

export const SITUACAO_VERSAO = {
    RASCUNHO: {label: 'Rascunho', color: 'info'},
    A_VENDA: {label: 'À venda', color: 'success'},
    EM_USO: {label: 'Fora de venda', color: 'warning'},
    ENCERRADA: {label: 'Encerrada', color: 'default'},
};

export const MODALIDADES = [
    {value: 'PRE_PAGO', label: 'Pré-pago', texto: 'A mensalidade cobra o ciclo seguinte, antes do uso.'},
    {value: 'POS_PAGO', label: 'Pós-pago', texto: 'A mensalidade cobra o ciclo que fechou.'},
];

export const MEIOS = [
    {value: 'BOLETO', label: 'Boleto com Pix', texto: 'Boleto com QR Pix, registrado no BB a cada ciclo.'},
    {value: 'CARTAO', label: 'Cartão', texto: 'Cartão tokenizado, cobrado na emissão da fatura.'},
];

export const SITUACOES_DO_MODULO = [
    {value: 'INCLUIDO', label: 'Incluído'},
    {value: 'OPCIONAL', label: 'Opcional'},
    {value: 'FORA', label: 'Fora'},
];

export const TRIAL_MAXIMO = 60;

export const getSituacaoVersao = (situacao) => SITUACAO_VERSAO[situacao] || {label: situacao || '-', color: 'default'};
export const getModalidadeLabel = (valor) => MODALIDADES.find((item) => item.value === valor)?.label || valor || '-';
export const getMeioLabel = (valor) => MEIOS.find((item) => item.value === valor)?.label || valor || '-';

const numeroOuZero = (valor) => {
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : 0;
};

// A versão que a lista e o endereço do plano mostram primeiro: a à venda; sem ela, o rascunho; sem ele, a última.
export const versaoPrincipal = (versoes = []) => (
    versoes.find((versao) => versao.situacao === 'A_VENDA')
    || versoes.find((versao) => versao.situacao === 'RASCUNHO')
    || versoes[versoes.length - 1]
    || null
);

export const rascunhoDe = (versoes = []) => versoes.find((versao) => versao.situacao === 'RASCUNHO') || null;

// A base de comparação da edição pelo CRUD do plano, como o app-funcionalidades: a à venda, senão a última publicada.
export const versaoBase = (versoes = []) => {
    const publicadas = versoes.filter((versao) => versao.situacao !== 'RASCUNHO');
    return versoes.find((versao) => versao.situacao === 'A_VENDA') || publicadas[publicadas.length - 1] || null;
};

export const proximoNumero = (versoes = []) => versoes.reduce((maior, versao) => Math.max(maior, versao.numero || 0), 0) + 1;

/* ---------- rascunho ---------- */

// O formulário do rascunho, a partir da versão que a API devolve. O módulo ausente da versão está fora.
export const formDaVersao = (versao) => ({
    modalidade: versao?.modalidade || 'PRE_PAGO',
    meios_aceitos: [...(versao?.meios_aceitos || [])],
    dias_trial: versao?.dias_trial ?? 0,
    preco_base_mensal: versao?.preco_base_mensal ?? 0,
    modulos: (versao?.modulos || []).reduce((acc, item) => {
        acc[item.id_modulo] = {situacao: item.situacao, preco_opcional: item.preco_opcional ?? ''};
        return acc;
    }, {}),
});

/*
 * Muda os meios aceitos. Sem cartão, o trial zera (não há como converter com cobrança automática): a tela avisa
 * com `trialZerado`, e o PUT leva `dias_trial: 0` junto, que a API exige.
 */
export const alternarMeio = (form, meio) => {
    const meios = form.meios_aceitos.includes(meio)
        ? form.meios_aceitos.filter((item) => item !== meio)
        : [...form.meios_aceitos, meio];
    const semCartao = !meios.includes('CARTAO');
    const trialZerado = semCartao && numeroOuZero(form.dias_trial) > 0 ? numeroOuZero(form.dias_trial) : 0;
    return {
        form: {...form, meios_aceitos: MEIOS.map((item) => item.value).filter((valor) => meios.includes(valor)), dias_trial: semCartao ? 0 : form.dias_trial},
        trialZerado,
    };
};

export const definirSituacaoDoModulo = (form, idModulo, situacao) => {
    const modulos = {...form.modulos};
    if (situacao === 'FORA') delete modulos[idModulo];
    else modulos[idModulo] = {situacao, preco_opcional: situacao === 'OPCIONAL' ? (modulos[idModulo]?.preco_opcional ?? '') : ''};
    return {...form, modulos};
};

export const definirPrecoDoOpcional = (form, idModulo, preco) => ({
    ...form,
    modulos: {...form.modulos, [idModulo]: {...form.modulos[idModulo], preco_opcional: preco}},
});

export const situacaoDoModulo = (form, idModulo) => form?.modulos?.[idModulo]?.situacao || 'FORA';

const precoParaApi = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return null;
    const numero = Number(String(valor).replace(',', '.'));
    return Number.isFinite(numero) ? numero : null;
};

// Corpo do PUT do rascunho. `dias_trial` vai inteiro (a API recusa texto e número quebrado).
export const corpoDoRascunho = (form) => ({
    modalidade: form.modalidade,
    meios_aceitos: form.meios_aceitos,
    dias_trial: Math.max(0, Math.min(TRIAL_MAXIMO, Math.trunc(numeroOuZero(form.dias_trial)))),
    preco_base_mensal: precoParaApi(form.preco_base_mensal) ?? 0,
    modulos: Object.entries(form.modulos).map(([idModulo, item]) => (item.situacao === 'OPCIONAL'
        ? {id_modulo: idModulo, situacao: 'OPCIONAL', preco_opcional: precoParaApi(item.preco_opcional)}
        : {id_modulo: idModulo, situacao: 'INCLUIDO'})),
});

// O rascunho mudou em relação ao que a API tem?
export const rascunhoAlterado = (form, versao) => JSON.stringify(corpoDoRascunho(form)) !== JSON.stringify(corpoDoRascunho(formDaVersao(versao)));

// O que a tela já sabe que impede publicar, antes de ir à API (que confere tudo de novo).
export const avisosDoRascunho = (form) => {
    const avisos = [];
    if (!form.meios_aceitos.length) avisos.push('Escolha pelo menos um meio de pagamento.');
    if (!Object.values(form.modulos).some((item) => item.situacao === 'INCLUIDO')) avisos.push('Inclua pelo menos um módulo.');
    Object.values(form.modulos)
        .filter((item) => item.situacao === 'OPCIONAL' && !(precoParaApi(item.preco_opcional) > 0))
        .forEach(() => avisos.push('Há opcional sem preço: o opcional precisa de preço maior que zero.'));
    return [...new Set(avisos)];
};

/* ---------- plano ---------- */

/*
 * Corpo do PUT do plano, para mudar só o que é do plano (nome, descrição, vitrine, perfis-modelo).
 *
 * O PUT do app-funcionalidades troca o cadastro inteiro, e preço, modalidade e módulos diferentes da versão que vale
 * viram uma versão nova, publicada na hora (modo compatível da BBF-104). Por isso o corpo repete exatamente a versão
 * base: a à venda, ou a última publicada, ou, num plano que nunca publicou, o próprio rascunho (que o PUT copia do
 * plano). Assim nada comercial muda e nenhuma versão nasce daqui.
 */
export const corpoDoPlano = (plano, versaoQueVale, mudancas = {}) => {
    const corpo = {
        id_sistema: plano.id_sistema,
        nome_plano: plano.nome_plano,
        descricao: plano.descricao ?? '',
        preco_base_mensal: numeroOuZero(versaoQueVale?.preco_base_mensal ?? plano.preco_base_mensal),
        tipo_cobranca: versaoQueVale?.modalidade || plano.tipo_cobranca,
        permite_addons: Boolean(plano.permite_addons),
        possui_suporte: plano.possui_suporte ?? true,
        status: plano.status || 'ATIVO',
        vitrine: Boolean(plano.vitrine),
        ...mudancas,
    };
    if (versaoQueVale?.modulos) {
        corpo.modulos = versaoQueVale.modulos.map((item) => ({
            id_modulo: item.id_modulo,
            incluso_por_padrao: item.situacao === 'INCLUIDO',
        }));
    }
    return corpo;
};

// O plano como o editor de perfis-modelo espera: o direito são os módulos incluídos da versão que vale.
export const formDosPerfis = (plano, versaoQueVale, perfisModelo) => ({
    id_sistema: plano.id_sistema,
    modulos: (versaoQueVale?.modulos || []).map((item) => ({id_modulo: item.id_modulo, incluso_por_padrao: item.situacao === 'INCLUIDO'})),
    perfis_modelo: perfisModelo,
});

/* ---------- assinaturas ---------- */

export const assinaturasPorVersao = (licencas = []) => licencas.reduce((acc, licenca) => {
    const chave = licenca.id_plano_versao || 'sem-versao';
    (acc[chave] = acc[chave] || []).push(licenca);
    return acc;
}, {});

// Sistema de uso interno não tem plano à venda: fica fora da lista de planos, como no protótipo.
export const ehSistemaInterno = (sistema) => Boolean(sistema?.uso_interno) || sistema?.status === 'INTERNO';

// Planos agrupados por sistema, na ordem do nome do sistema.
export const agruparPorSistema = (planos = []) => {
    const grupos = new Map();
    planos.forEach((plano) => {
        const sistema = plano.sistema || {id: plano.id_sistema, nome: 'Sistema'};
        if (!grupos.has(sistema.id)) grupos.set(sistema.id, {sistema, planos: []});
        grupos.get(sistema.id).planos.push(plano);
    });
    return [...grupos.values()].sort((a, b) => (a.sistema.nome || '').localeCompare(b.sistema.nome || '', 'pt-BR'));
};

/* ---------- textos ---------- */

export const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

// O que acontece com a versão à venda de hoje quando esta é publicada; vazio quando não há outra à venda.
export const efeitoNaAnterior = (versao, versoes = []) => {
    const aVenda = versoes.find((item) => item.situacao === 'A_VENDA' && item.numero !== versao.numero);
    if (!aVenda) return '';
    const n = aVenda.assinaturas || 0;
    return n
        ? `A versão ${aVenda.numero} sai de venda e segue só para quem já assina (${plural(n, 'assinatura', 'assinaturas')}).`
        : `A versão ${aVenda.numero}, sem assinaturas, fica encerrada.`;
};

// O que acontece ao publicar, para a pessoa ver antes de confirmar.
export const efeitoDePublicar = (versao, versoes = []) => {
    const anterior = efeitoNaAnterior(versao, versoes);
    return `A versão ${versao.numero} passa a ser a versão à venda do plano.${anterior ? ` ${anterior}` : ''}`;
};
